import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { getAllDaan, getUsers } from "../services/auth";
import LoadingScreen from "../components/Lavajam/LoadingScreen";
import ErrorScreen from "../components/Lavajam/ErrorScreen";
import Header from "../components/Lavajam/Header";
import FiltersSection from "../components/Lavajam/FiltersSection";
import StatsCards from "../components/Lavajam/StatsCards";
import DaanTable from "../components/Daan/DataTable";
import * as XLSX from "xlsx";

function Daan() {
  const [daanData, setDaanData] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [createdById, setCreatedById] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchDaanData = useCallback(async (userId, start, end, createdById) => {
    setLoading(true);
    setError(null);
    try {
      const effectiveStartDate = start || "2020-01-01";
      const effectiveEndDate = end || "2030-12-31";
      const response = await getAllDaan(
        userId,
        effectiveStartDate,
        effectiveEndDate,
        createdById,
      );

      if (response?.entries && Array.isArray(response.entries)) {
        setDaanData(response.entries);
        setCurrentPage(1);
      } else {
        setDaanData([]);
        setError("Received unexpected data format for daan entries.");
      }
    } catch (err) {
      console.error("Error fetching daan data:", err);
      setError("Failed to load data. Please try again.");
      setDaanData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsers();
      if (response?.users && Array.isArray(response.users)) {
        setUsers(response.users);
        const initialUser = response.users.find((u) => u.ID === selectedUserId);
        if (initialUser) {
          setUserSearchTerm(initialUser.pName);
        }
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, [selectedUserId]);

  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    setStartDate(`${year}-01-01`);
    setEndDate(`${year}-12-31`);
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchDaanData(selectedUserId, startDate, endDate, createdById);
    }
  }, [selectedUserId, startDate, endDate, createdById, fetchDaanData]);

  const totalAmount = useMemo(() => {
    return daanData.reduce((sum, entry) => sum + (Number(entry.Amt) || 0), 0);
  }, [daanData]);

  const totalEntries = useMemo(() => daanData.length, [daanData]);

  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return users;
    return users.filter((user) =>
      user.pName.toLowerCase().includes(userSearchTerm.toLowerCase()),
    );
  }, [users, userSearchTerm]);

  const adminUsers = useMemo(() => {
    return users.filter((user) => user.Designation === "admin");
  }, [users]);

  const totalPages = Math.ceil(daanData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = daanData.slice(startIndex, endIndex);

  const handleUserSelect = (userId, userName) => {
    setSelectedUserId(userId);
    setUserSearchTerm(userName);
    setIsDropdownOpen(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSelectedUserId(0);
    setUserSearchTerm("");
    setCreatedById("all");
    const today = new Date();
    const year = today.getFullYear();
    setStartDate(`${year}-01-01`);
    setEndDate(`${year}-12-31`);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(daanData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daan Data");
    XLSX.writeFile(workbook, "daan_data.xlsx");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading && daanData.length === 0) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen error={error} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        <Header title="Daan Entries" />

        <FiltersSection
          dropdownRef={dropdownRef}
          userSearchTerm={userSearchTerm}
          setUserSearchTerm={setUserSearchTerm}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          filteredUsers={filteredUsers}
          handleUserSelect={handleUserSelect}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          resetFilters={resetFilters}
          createdById={createdById}
          setCreatedById={setCreatedById}
          createdByList={adminUsers}
        />

        <StatsCards
          totalAmount={totalAmount}
          totalEntries={totalEntries}
          showNotebook={false}
        />

        <div className="mb-4 flex justify-end">
          <button
            onClick={handleDownloadExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Download Excel
          </button>
        </div>

        <DaanTable
          daanData={daanData}
          loading={loading}
          currentData={currentData}
          itemsPerPage={itemsPerPage}
          handleItemsPerPageChange={handleItemsPerPageChange}
          startIndex={startIndex}
          endIndex={endIndex}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      </div>
    </div>
  );
}

export default Daan;
